import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBranches } from './publicApi';
import {
  bookingPathForBrand,
  branchForBrand,
  brandPath,
  formatThaiPhoneDisplay,
  locationForBrand,
  photosForBrand,
  usePublicBrand,
} from './PublicBrand';
import {
  BOUTIQUE_CONTACT,
  NIRANA_CONTACT,
  SAANTI_CONTACT,
  SERVICE_COPY,
  SpaFooter,
  SpaNav,
} from './SpaHome';
import SpaArrow from './SpaArrow';
import Seo, {
  localBusinessSchemas,
  serviceListSchema,
} from './Seo';
import { groupServicesByCategory } from './serviceGroups';
import usePublicServices from './usePublicServices';
import './spa.css';

const money = (n) => new Intl.NumberFormat('th-TH').format(Number(n || 0));

const SAANTI_PHOTOS = photosForBrand('saanti');
const BOUTIQUE_PHOTOS = photosForBrand('saanti-boutique');
const NIRANA_PHOTOS = photosForBrand('nirana');

function PageShell({
  className = '',
  eyebrow,
  title,
  intro,
  heroImage,
  heroAlt,
  seoTitle,
  seoDescription = intro,
  canonicalPath,
  structuredData = [],
  children,
}) {
  const { brand } = usePublicBrand();
  const location = locationForBrand(brand);
  const propertyClass = brand === 'nirana'
    ? 'spa-nirana'
    : brand === 'saanti-boutique' ? 'spa-boutique' : '';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return (
    <div className={`spa${propertyClass ? ` ${propertyClass}` : ''}${className ? ` ${className}` : ''}`}>
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonicalPath={canonicalPath}
        image={heroImage}
        imageAlt={heroAlt}
        breadcrumbs={[
          { name: 'Home', path: brandPath('/', brand) },
          { name: eyebrow, path: canonicalPath },
        ]}
        structuredData={structuredData}
      />
      <SpaNav />
      <header className={`spa-page-hero${heroImage ? '' : ' spa-page-hero-placeholder'}`}>
        {heroImage ? (
          <img src={heroImage} alt={heroAlt} fetchPriority="high" decoding="async" />
        ) : (
          <div className="spa-page-hero-placeholder-mark" aria-hidden="true">
            {location.shortLabel}
          </div>
        )}
        <div className="spa-page-hero-shade" />
        <div className="wrap spa-page-hero-content">
          <div className="spa-kicker">{eyebrow}</div>
          <h1>{title}</h1>
          <p>{intro}</p>
        </div>
      </header>
      <main>{children}</main>
      <SpaFooter />
    </div>
  );
}

function BookingBand({ title = 'Make time for yourself.', to = null }) {
  const { brand } = usePublicBrand();
  const bookingPath = to || bookingPathForBrand(null, brand);
  return (
    <section className="spa-booking-band">
      <div className="wrap">
        <div>
          <div className="eyebrow">Your quiet hour awaits</div>
          <h2>{title}</h2>
        </div>
        <Link className="spa-cta light spa-button-link" to={bookingPath}>Book online</Link>
      </div>
    </section>
  );
}

const STORY_CONTENT = {
  saanti: {
    title: 'A quiet idea, brought to life.',
    intro: 'SAANTI began with a belief that genuine care, Thai healing traditions and a beautiful old neighbourhood belong together.',
    hero: SAANTI_PHOTOS.hero,
    heading: 'A place to pause from everyday life.',
    label: 'How SAANTI began',
    body: [
      'Our journey began with a deep passion for wellness and holistic healing. More than a decade in the service industry shaped a heartfelt dedication to therapeutic care.',
      'We envisioned a space where people could step away from the chaos of everyday life—to slow down, breathe deeply and reconnect with themselves.',
      'That vision now lives as SAANTI Thai Massage, tucked inside a beautifully preserved heritage building in the heart of Song Wat Road.',
      'Inspired by the timeless character of the neighbourhood and the ancient art of Thai massage, we blend time-honoured techniques with a nostalgic atmosphere.',
    ],
    images: SAANTI_PHOTOS.story,
    quote: 'Here, we believe that self-care is sacred.',
    values: ['Attentive care', 'Thai tradition', 'A sense of place'],
  },
  nirana: {
    title: 'A quiet space in the heart of Song Wat.',
    intro: 'With spacious interiors and a complete range of massage and spa treatments, NIRANA is a place to step in, slow down and feel restored.',
    hero: NIRANA_PHOTOS.hero,
    heading: 'Step in. Slow down. Feel restored.',
    label: 'The NIRANA story',
    body: [
      'NIRANA is a quiet space in the heart of Song Wat.',
      'Spacious interiors and a complete range of massage and spa treatments create room to pause from the pace of the city.',
      'Guests can choose from the live treatment menu, settle into the calm surroundings and reserve directly with the NIRANA team.',
      'Step in. Slow down. Feel restored.',
    ],
    images: NIRANA_PHOTOS.story,
    quote: 'A quiet space in the heart of Song Wat.',
    values: ['Spacious interiors', 'Complete treatments', 'Song Wat setting'],
    valueCopy: [
      'Open, calming interiors give each visit room to feel unhurried.',
      'Massage and spa treatments are brought together in one complete live menu.',
      'A convenient retreat in Bangkok’s historic Song Wat neighbourhood.',
    ],
  },
  'saanti-boutique': {
    title: 'A quiet retreat in the heart of old Bangkok.',
    intro: 'Just steps from Yaowarat, SAANTI Boutique Thai Massage House is tucked inside Baan 2459 Heritage Boutique Hotel.',
    hero: BOUTIQUE_PHOTOS.hero,
    heading: 'Thai healing traditions in a historic home.',
    label: 'The Boutique story',
    body: [
      'Just steps away from the vibrant streets of Yaowarat, SAANTI Boutique Thai Massage is quietly tucked inside Baan 2459 Heritage Boutique Hotel—a century-old Thai home surrounded by lush greenery and timeless charm.',
      'Here, the art of traditional Thai massage meets the serenity of a historic setting.',
      'Peaceful, intimate, and away from the city’s rhythm, SAANTI offers more than a massage—it is a moment to slow down, unwind, and reconnect with a sense of calm.',
      'A quiet retreat in the heart of old Bangkok.',
    ],
    images: BOUTIQUE_PHOTOS.story,
    quote: 'A quiet retreat in the heart of old Bangkok.',
    values: ['Historic setting', 'Traditional Thai massage', 'Intimate calm'],
    valueCopy: [
      'Baan 2459 is a century-old Thai home surrounded by greenery and timeless character.',
      'Time-honoured massage care is at the heart of the Boutique experience.',
      'A peaceful setting designed for slowing down and reconnecting with calm.',
    ],
  },
};

export function SpaStory() {
  const { brand } = usePublicBrand();
  const location = locationForBrand(brand);
  const story = STORY_CONTENT[brand] || STORY_CONTENT.saanti;
  const storyValueCopy = brand === 'saanti' ? [
    'Every visit is paced around the guest, with warmth and respect from arrival to farewell.',
    'Time-honoured techniques are practiced with purpose, skill and an understanding of the body.',
    'Our Song Wat home preserves the quiet character and history of Bangkok’s old town.',
  ] : story.valueCopy;

  return (
    <PageShell
      eyebrow="Our story"
      title={story.title}
      intro={story.intro}
      heroImage={story.hero}
      heroAlt={`${location.label} story`}
      seoTitle={`Our Story | ${location.label}`}
      seoDescription={story.intro}
      canonicalPath={`/story?brand=${brand}`}
    >
      <section className="spa-page-section">
        <div className="wrap spa-story-layout">
          <div>
            <div className="spa-section-label"><span>01</span> {story.label}</div>
            <h2>{story.heading}</h2>
          </div>
          <div className="spa-rich-copy">
            {story.body.map((paragraph, index) => (
              <p className={index === 0 ? 'lead' : ''} key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="spa-story-images">
        <div className="wrap">
          <figure className={`wide${story.images[0] ? '' : ' spa-content-skeleton'}`}>
            {story.images[0]
              ? <img src={story.images[0]} alt={`${location.shortLabel} property`} loading="lazy" decoding="async" />
              : <div><span>Photography coming soon</span></div>}
          </figure>
          <figure className={story.images[1] ? '' : 'spa-content-skeleton'}>
            {story.images[1]
              ? <img src={story.images[1]} alt={`${location.shortLabel} experience`} loading="lazy" decoding="async" />
              : <div><span>Photography coming soon</span></div>}
          </figure>
          <blockquote>
            “{story.quote}”
            <span>{location.label}</span>
          </blockquote>
        </div>
      </section>

      <section className="spa-values">
        <div className="wrap">
          {story.values.map((heading, index) => (
            <article key={heading}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{heading}</h3>
              <p>{storyValueCopy[index]}</p>
            </article>
          ))}
        </div>
      </section>
      <BookingBand title={`Experience ${location.shortLabel} for yourself.`} />
    </PageShell>
  );
}

const MASSAGE_PAGE_CONTENT = {
  saanti: {
    title: 'Find the care your body is asking for.',
    intro: 'Each massage has its own rhythm and focus. This guide helps you choose an experience that feels right for you.',
    hero: SAANTI_PHOTOS.treatments[0],
  },
  nirana: {
    title: 'Find your NIRANA treatment rhythm.',
    intro: 'Explore different rhythms of massage, understand pressure and focus, and choose the kind of care that feels right today.',
    hero: NIRANA_PHOTOS.treatments[1],
  },
  'saanti-boutique': {
    title: 'Traditional Thai massage in a historic setting.',
    intro: 'Inside Baan 2459 Heritage Boutique Hotel, time-honoured Thai massage meets a peaceful and intimate old-Bangkok atmosphere.',
    hero: BOUTIQUE_PHOTOS.hero,
  },
};

const MASSAGE_GOALS = [
  {
    number: '01',
    title: 'I want to deeply unwind',
    copy: 'Choose a flowing oil-based ritual or gentle warming treatment, with light-to-medium pressure and an unhurried rhythm.',
  },
  {
    number: '02',
    title: 'My body feels stiff',
    copy: 'Traditional Thai massage combines steady compression and assisted stretching to create a sense of space and mobility.',
  },
  {
    number: '03',
    title: 'I have a focused area',
    copy: 'A targeted treatment can spend more time around the shoulders, back, legs or feet. Tell your therapist where you need attention.',
  },
];

const MASSAGE_GUIDE = [
  {
    label: 'Movement & balance',
    title: 'Traditional Thai massage',
    copy: 'Performed without oil, Thai massage uses rhythmic palm and thumb pressure together with supported stretches. It is an active, grounding experience that can be adapted from gentle to firm.',
    pressure: 'Medium to firm',
    style: 'Compression and assisted stretching',
    choose: 'When you feel stiff or prefer an energising treatment',
  },
  {
    label: 'Calm & restoration',
    title: 'Aroma oil massage',
    copy: 'Warm oil and long, flowing movements create a slower full-body ritual. Pressure can remain soft and soothing or become more focused where your body feels tense.',
    pressure: 'Light to medium',
    style: 'Flowing oil-based movements',
    choose: 'When relaxation and a quieter pace matter most',
  },
  {
    label: 'Focused care',
    title: 'Deep tissue & upper-body work',
    copy: 'Slower, more concentrated work gives the therapist time to focus on areas such as the back, neck and shoulders. Firm does not need to mean painful—your feedback guides the pressure.',
    pressure: 'Medium to firm',
    style: 'Slow and targeted pressure',
    choose: 'When one area needs more detailed attention',
  },
  {
    label: 'Grounding & warmth',
    title: 'Foot massage & special rituals',
    copy: 'Foot-focused care offers an easy way to reset after a long day, while warming rituals such as hot stone create a deeper sense of comfort. Available options differ by branch.',
    pressure: 'Gentle to firm',
    style: 'Focused pressure or soothing warmth',
    choose: 'When your feet feel tired or you want a comforting ritual',
  },
];

export function SpaMassage() {
  const { brand } = usePublicBrand();
  const location = locationForBrand(brand);
  const massagePage = MASSAGE_PAGE_CONTENT[brand] || MASSAGE_PAGE_CONTENT.saanti;
  const propertyPhotos = photosForBrand(brand);
  const servicesPath = brandPath('/services', brand);

  return (
    <PageShell
      eyebrow="About massage"
      title={massagePage.title}
      intro={massagePage.intro}
      heroImage={massagePage.hero}
      heroAlt={`${location.label} massage guide`}
      seoTitle={`Massage Guide | ${location.label}`}
      seoDescription={massagePage.intro}
      canonicalPath={`/massage?brand=${brand}`}
    >
      <section className="spa-page-section spa-massage-intro">
        <div className="wrap">
          <div className="spa-section-label"><span>01</span> Choosing a treatment</div>
          <div className="spa-title-row dark">
            <h2>Start with how<br />you want to feel.</h2>
            <p>
              You do not need to know every technique before you arrive. Think
              about the pace, pressure and focus that feel right today, then
              share that with your {location.shortLabel} therapist.
            </p>
          </div>
          <div className="spa-massage-goals">
            {MASSAGE_GOALS.map((goal) => (
              <article key={goal.number}>
                <span>{goal.number}</span>
                <h3>{goal.title}</h3>
                <p>{goal.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="spa-massage-guide">
        <div className="wrap">
          <div className="spa-section-label"><span>02</span> Understand the styles</div>
          <div className="spa-massage-guide-list">
            {MASSAGE_GUIDE.map((item, index) => {
              const image = propertyPhotos.treatments.length
                ? propertyPhotos.treatments[index % propertyPhotos.treatments.length]
                : propertyPhotos.hero;
              return (
                <article key={item.title}>
                  {image ? (
                    <figure><img src={image} alt={`${item.title} at ${location.shortLabel}`} loading="lazy" decoding="async" /></figure>
                  ) : (
                    <div className="spa-content-skeleton"><div><span>Image coming soon</span></div></div>
                  )}
                  <div className="spa-massage-guide-copy">
                    <div className="eyebrow">{item.label}</div>
                    <h2>{item.title}</h2>
                    <p>{item.copy}</p>
                    <dl>
                      <div><dt>Typical pressure</dt><dd>{item.pressure}</dd></div>
                      <div><dt>Style</dt><dd>{item.style}</dd></div>
                      <div><dt>Choose it when</dt><dd>{item.choose}</dd></div>
                    </dl>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="spa-massage-prepare">
        <div className="wrap">
          <div className="spa-section-label"><span>03</span> Before your visit</div>
          <div className="spa-massage-prepare-grid">
            <div>
              <h2>A better massage begins with a conversation.</h2>
              <p>Tell us what feels comfortable. Your therapist can adjust the pressure, spend more time on a particular area, or avoid anything sensitive.</p>
              <Link className="spa-text-link" to={servicesPath}>View current services &amp; prices <SpaArrow /></Link>
            </div>
            <ol>
              <li><span>01</span><div><b>Before</b><p>Share injuries, pregnancy, health considerations and any areas you would like us to avoid.</p></div></li>
              <li><span>02</span><div><b>During</b><p>Speak up whenever you would like lighter or firmer pressure. Comfort is part of good care.</p></div></li>
              <li><span>03</span><div><b>After</b><p>Move slowly, drink water and give yourself a little time before returning to a busy day.</p></div></li>
            </ol>
          </div>
          <p className="spa-care-note">
            Massage is a wellness service and is not a substitute for medical
            care. If you are pregnant, injured or managing a health condition,
            please tell us before your treatment.
          </p>
        </div>
      </section>
      <BookingBand />
    </PageShell>
  );
}

export function SpaServices() {
  const { brand } = usePublicBrand();
  const location = locationForBrand(brand);
  const {
    branchCode,
    branchError,
    branches,
    propertyUnavailable: missingBrandBranch,
    serviceError: error,
    services,
  } = usePublicServices(brand);
  const isNirana = brand === 'nirana';
  const isBoutique = brand === 'saanti-boutique';
  const brandName = location.shortLabel;
  const propertyPhotos = photosForBrand(brand);

  const bookingPath = bookingPathForBrand(branches, brand);

  return (
    <PageShell
      eyebrow="Our services"
      title="Treatments, times and prices."
      intro={`Everything shown here is the live treatment menu activated specifically for ${location.label}.`}
      heroImage={propertyPhotos.treatments[0] || propertyPhotos.hero}
      heroAlt={isNirana ? 'A massage treatment at NIRANA' : isBoutique ? 'SAANTI Boutique treatment menu' : 'Aroma oil massage at SAANTI'}
      seoTitle={`Massage Services & Prices | ${location.label}`}
      seoDescription={`View current treatments, durations and prices at ${location.label}, then reserve your preferred time online.`}
      canonicalPath={`/services?brand=${brand}`}
      structuredData={services ? [serviceListSchema(services, branchCode, brand)] : []}
    >
      <section className="spa-page-section spa-services-page">
        <div className="wrap">
          <div className="spa-services-toolbar">
            <div>
              <div className="spa-section-label"><span>01</span> Live treatment menu</div>
              <h2>{brandName} treatments.</h2>
              <p className="spa-services-property-note">
                Branch · {location.branchName}
              </p>
            </div>
          </div>

          {(error || branchError) && <div className="spa-error">{error || branchError}</div>}
          {missingBrandBranch && !branchError && (
            <div className="spa-error">
              {location.label} is not accepting online bookings right now. Please contact the property for assistance.
            </div>
          )}
          {!services && !error && !branchError && !missingBrandBranch && <div className="spa-loading">Loading treatments…</div>}
          {services?.length === 0 && (
            <div className="spa-note">No treatments are currently activated online for {location.label}.</div>
          )}

          <div className="spa-service-sections">
            {groupServicesByCategory(services || []).map((group) => (
              <section className="spa-service-category" key={group.key}>
                <header className="spa-service-category-head">
                  <span>Service category</span>
                  <h3>{group.nameEn}</h3>
                  {group.nameTh && group.nameTh !== group.nameEn && <p>{group.nameTh}</p>}
                </header>
                <div className="spa-service-list">
            {group.services.map((service) => {
              const index = (services || []).findIndex((item) => item.id === service.id);
              const fallbackImage = propertyPhotos.treatments.length
                ? propertyPhotos.treatments[index % propertyPhotos.treatments.length]
                : null;
              const serviceImage = service.image_url || fallbackImage;
              return (
              <article key={service.id}>
                {serviceImage ? (
                  <img
                    src={serviceImage}
                    alt={`${service.name_en} at ${brandName}`}
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="spa-content-skeleton spa-service-photo-placeholder">
                    <div><span>Photography coming soon</span></div>
                  </div>
                )}
                <div className="spa-service-list-copy">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h3>{service.name_en}</h3>
                    {service.name_th && <div className="th">{service.name_th}</div>}
                    <p>{service.description || SERVICE_COPY[service.code] || 'A restorative treatment delivered with care and attention.'}</p>
                  </div>
                </div>
                <div className="spa-service-variants">
                  {(service.variants || []).map((variant) => (
                    <div key={variant.id}>
                      <span>{variant.durationMinutes} min</span>
                      <b>{money(variant.price)} ฿</b>
                    </div>
                  ))}
                  <Link className="spa-cta spa-button-link" to={bookingPath}>
                    Book
                  </Link>
                </div>
              </article>
              );
            })}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>
      <BookingBand title="Ready to choose your time?" to={bookingPath} />
    </PageShell>
  );
}

const FACILITIES = [
  {
    name: 'Foot Massage Room',
    image: SAANTI_PHOTOS.facilities[0],
    copy: 'A comfortable shared room prepared for grounding foot treatments and a quiet pause after exploring Song Wat.',
  },
  {
    name: 'Thai Massage Room',
    image: SAANTI_PHOTOS.facilities[1],
    copy: 'A calm, carefully prepared space with room for the stretching and movement of traditional Thai massage.',
  },
  {
    name: 'Aroma Room',
    image: SAANTI_PHOTOS.facilities[2],
    copy: 'A dedicated treatment room designed for privacy and an undisturbed aroma-oil massage experience.',
  },
];

const NIRANA_FACILITIES = [
  { name: 'Foot Massage Lounge', image: NIRANA_PHOTOS.facilities[0], copy: 'A dedicated lounge arranged for grounding foot and lower-leg treatments.' },
  { name: 'Thai Massage Room', image: NIRANA_PHOTOS.facilities[1], copy: 'A calm treatment room with space for the movement and stretching of Thai massage.' },
  { name: 'Private Treatment Room', image: NIRANA_PHOTOS.facilities[2], copy: 'A private setting for oil massage, body care and longer spa rituals.' },
];

const BOUTIQUE_FACILITIES = [
  {
    name: 'Foot Massage Lounge',
    image: BOUTIQUE_PHOTOS.facilities[0],
    copy: 'A bright, unhurried lounge prepared for grounding foot and lower-leg treatments.',
  },
  {
    name: 'Heritage Thai Massage Room',
    image: BOUTIQUE_PHOTOS.facilities[1],
    copy: 'Warm brickwork and the character of Baan 2459 frame a peaceful space for traditional Thai massage.',
  },
  {
    name: 'Private Treatment Room',
    image: BOUTIQUE_PHOTOS.facilities[2],
    copy: 'An intimate room designed for privacy, attentive care and a quiet pause away from the city.',
  },
];

const FACILITY_PAGE_CONTENT = {
  saanti: {
    title: 'The serenity experience.',
    intro: 'Our heritage home is arranged with care for comfort, calm and privacy—from soft seating to dedicated treatment rooms.',
    hero: SAANTI_PHOTOS.facilitiesHero,
    label: 'Inside SAANTI',
    list: FACILITIES,
    gallery: SAANTI_PHOTOS.gallery,
  },
  nirana: {
    title: 'Rooms made for a quieter pause.',
    intro: 'Explore NIRANA’s treatment rooms, foot lounge and calm shared spaces.',
    hero: NIRANA_PHOTOS.facilitiesHero,
    label: 'Inside NIRANA',
    list: NIRANA_FACILITIES,
    gallery: NIRANA_PHOTOS.gallery,
  },
  'saanti-boutique': {
    title: 'A heritage setting near Yaowarat.',
    intro: 'SAANTI Boutique is tucked inside Baan 2459, a century-old Thai home surrounded by greenery, warm interiors and timeless character.',
    hero: BOUTIQUE_PHOTOS.facilitiesHero,
    label: 'Inside the Boutique',
    list: BOUTIQUE_FACILITIES,
    gallery: BOUTIQUE_PHOTOS.gallery,
  },
};

export function SpaFacilities() {
  const { brand } = usePublicBrand();
  const location = locationForBrand(brand);
  const facilityPage = FACILITY_PAGE_CONTENT[brand] || FACILITY_PAGE_CONTENT.saanti;

  return (
    <PageShell
      eyebrow="Facilities"
      title={facilityPage.title}
      intro={facilityPage.intro}
      heroImage={facilityPage.hero}
      heroAlt={`${location.label} facilities`}
      seoTitle={`Massage Rooms & Facilities | ${location.label}`}
      seoDescription={facilityPage.intro}
      canonicalPath={`/facilities?brand=${brand}`}
    >
      <section className="spa-page-section spa-facilities-page">
        <div className="wrap">
          <div className="spa-section-label"><span>01</span> {facilityPage.label}</div>
          <div className="spa-facility-list">
            {facilityPage.list.map((facility, index) => (
              <article key={facility.name}>
                <figure className={facility.image ? '' : 'spa-content-skeleton'}>
                  {facility.image ? (
                    <img
                      src={facility.image}
                      alt={facility.name}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : <div><span>Room photography coming soon</span></div>}
                </figure>
                <div>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h2>{facility.name}</h2>
                  <p>{facility.copy}</p>
                  <Link className="spa-text-link" to={brandPath('/book', brand)}>Reserve a treatment <SpaArrow /></Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="spa-facility-gallery">
        {facilityPage.gallery.map((src, index) => (
          <figure className={src ? '' : 'spa-content-skeleton'} key={src || `placeholder-${index}`}>
            {src
              ? <img src={src} alt={`The ${location.shortLabel} experience ${index + 1}`} loading="lazy" decoding="async" />
              : <div><span>Image {String(index + 1).padStart(2, '0')}</span></div>}
          </figure>
        ))}
      </section>
      <BookingBand />
    </PageShell>
  );
}

export function SpaContact() {
  const { brand } = usePublicBrand();
  const { branches, error } = useBranches();
  const location = locationForBrand(brand);
  const isNirana = brand === 'nirana';
  const isBoutique = brand === 'saanti-boutique';
  const activeBranch = branchForBrand(branches, brand);
  const bookingPath = bookingPathForBrand(branches, brand);
  const visibleError = typeof error === 'string' && !/failed to fetch/i.test(error) ? error : '';
  const missingBranch = Array.isArray(branches) && !activeBranch;

  if (isBoutique) {
    const boutiqueBusiness = activeBranch ? [{
      ...activeBranch,
      address: BOUTIQUE_CONTACT.address,
      hours: '10:00 - 22:00',
      phone: BOUTIQUE_CONTACT.phoneDisplay,
    }] : [];

    return (
      <PageShell
        eyebrow="Contact"
        title="SAANTI Boutique, old Bangkok."
        intro="Visit our quiet Thai massage retreat inside Baan 2459 Heritage Boutique Hotel, just steps from Yaowarat."
        heroImage={BOUTIQUE_PHOTOS.contact}
        heroAlt="SAANTI Boutique Thai Massage House"
        seoTitle="Contact SAANTI Boutique Thai Massage House | Yaowarat"
        seoDescription="Contact and book SAANTI Boutique Thai Massage House at Baan 2459 near Yaowarat, Bangkok. Open daily 10:00–22:00."
        canonicalPath={`/contact?brand=${brand}`}
        structuredData={boutiqueBusiness.length ? localBusinessSchemas(boutiqueBusiness) : []}
      >
        <section className="spa-page-section spa-contact-page">
          <div className="wrap">
            <div className="spa-contact-intro">
              <div>
                <div className="spa-section-label"><span>01</span> Visit the Boutique</div>
                <h2>A quiet retreat<br />near Yaowarat.</h2>
              </div>
              <p>Open daily from 10:00 to 22:00 inside the century-old Baan 2459 Heritage Boutique Hotel.</p>
            </div>

            {visibleError && <div className="spa-error">{visibleError}</div>}
            {missingBranch && !error && (
              <div className="spa-error">SAANTI Boutique is not accepting online bookings right now.</div>
            )}
            <div className="spa-branch-grid single">
              <article>
                <div className="eyebrow">Baan 2459 Heritage Boutique Hotel</div>
                <h3>{location.branchName}</h3>
                <dl>
                  <div><dt>Address</dt><dd>{BOUTIQUE_CONTACT.address}</dd></div>
                  <div><dt>Hours</dt><dd>Daily {BOUTIQUE_CONTACT.hours}</dd></div>
                  <div><dt>Telephone</dt><dd><a href={BOUTIQUE_CONTACT.phoneHref}>{BOUTIQUE_CONTACT.phoneDisplay}</a></dd></div>
                  <div><dt>Line</dt><dd><a href={BOUTIQUE_CONTACT.lineHref} target="_blank" rel="noreferrer">{BOUTIQUE_CONTACT.lineDisplay}</a></dd></div>
                </dl>
                <div className="spa-contact-actions">
                  <Link className="spa-cta spa-button-link" to={bookingPath}>Book SAANTI Boutique</Link>
                  <a
                    className="spa-cta ghost spa-button-link"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BOUTIQUE_CONTACT.mapQuery)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Directions
                  </a>
                </div>
              </article>
            </div>

            <div className="spa-contact-channels">
              <div><span>Phone</span><a href={BOUTIQUE_CONTACT.phoneHref}>{BOUTIQUE_CONTACT.phoneDisplay}</a></div>
              <div><span>Line</span><a href={BOUTIQUE_CONTACT.lineHref} target="_blank" rel="noreferrer">{BOUTIQUE_CONTACT.lineDisplay}</a></div>
              <div><span>Email</span><a href={BOUTIQUE_CONTACT.emailHref}>{BOUTIQUE_CONTACT.emailDisplay}</a></div>
              <div>
                <span>Social</span>
                <a href={BOUTIQUE_CONTACT.instagramHref} target="_blank" rel="noreferrer">Instagram {BOUTIQUE_CONTACT.instagramDisplay}</a>
                <p>Facebook {BOUTIQUE_CONTACT.facebookDisplay}</p>
              </div>
            </div>
          </div>
        </section>
        <BookingBand title="Reserve your Boutique treatment." to={bookingPath} />
      </PageShell>
    );
  }

  if (isNirana) {
    const niranaBusiness = activeBranch ? [{
      ...activeBranch,
      address: NIRANA_CONTACT.address,
      hours: '10:00 - 22:00',
      phone: NIRANA_CONTACT.phoneDisplay,
    }] : [];

    return (
      <PageShell
        eyebrow="Contact us"
        title="Plan your NIRANA visit."
        intro="Reach NIRANA by phone, Line, Instagram, Facebook or email. We are open every day from 10:00 to 22:00."
        heroImage={NIRANA_PHOTOS.contact}
        heroAlt="A relaxing treatment at NIRANA Massage & Spa"
        seoTitle="Contact NIRANA Massage & Spa Bangkok | Hours & Booking"
        seoDescription="Contact NIRANA Massage & Spa in Bangkok by phone, Line, Instagram, Facebook or email. Open daily from 10:00 to 22:00."
        canonicalPath={`/contact?brand=${brand}`}
        structuredData={niranaBusiness.length ? localBusinessSchemas(niranaBusiness) : []}
      >
        <section className="spa-page-section spa-contact-page">
          <div className="wrap">
            <div className="spa-contact-intro">
              <div>
                <div className="spa-section-label"><span>01</span> Visit NIRANA</div>
                <h2>NIRANA,<br />Bangkok.</h2>
              </div>
              <p>
                Open every day from 10:00 to 22:00. Reserve online or contact
                our team directly for help planning your visit.
              </p>
            </div>

            {visibleError && <div className="spa-error">{visibleError}</div>}
            {missingBranch && !error && (
              <div className="spa-error">NIRANA is not accepting online bookings right now.</div>
            )}
            <div className="spa-branch-grid single">
              <article>
                <div className="eyebrow">NIRANA location</div>
                <h3>{location.branchName}</h3>
                <dl>
                  <div><dt>Address</dt><dd>{NIRANA_CONTACT.address}</dd></div>
                  <div><dt>Hours</dt><dd>Daily {NIRANA_CONTACT.hours}</dd></div>
                  <div><dt>Telephone</dt><dd><a href={NIRANA_CONTACT.phoneHref}>{NIRANA_CONTACT.phoneDisplay}</a></dd></div>
                  <div><dt>Line</dt><dd><a href={NIRANA_CONTACT.lineHref} target="_blank" rel="noreferrer">{NIRANA_CONTACT.lineDisplay}</a></dd></div>
                </dl>
                <div className="spa-contact-actions">
                  <Link className="spa-cta spa-button-link" to={bookingPath}>Book at NIRANA</Link>
                  <a
                    className="spa-cta ghost spa-button-link"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(NIRANA_CONTACT.mapQuery)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Directions
                  </a>
                </div>
              </article>
            </div>

            <div className="spa-contact-channels">
              <div>
                <span>Phone</span>
                <a href={NIRANA_CONTACT.phoneHref}>{NIRANA_CONTACT.phoneDisplay}</a>
              </div>
              <div>
                <span>Line</span>
                <a href={NIRANA_CONTACT.lineHref} target="_blank" rel="noreferrer">{NIRANA_CONTACT.lineDisplay}</a>
              </div>
              <div>
                <span>Email</span>
                <a href={NIRANA_CONTACT.emailHref}>{NIRANA_CONTACT.emailDisplay}</a>
              </div>
              <div>
                <span>Social</span>
                <a href={NIRANA_CONTACT.instagramHref} target="_blank" rel="noreferrer">Instagram {NIRANA_CONTACT.instagramDisplay}</a>
                <a href={NIRANA_CONTACT.facebookHref} target="_blank" rel="noreferrer">Facebook {NIRANA_CONTACT.facebookDisplay}</a>
              </div>
            </div>
          </div>
        </section>
        <BookingBand title="Plan your NIRANA visit." to={bookingPath} />
      </PageShell>
    );
  }

  return (
    <PageShell
      eyebrow="Contact us"
      title="Come find your quiet hour."
      intro="SAANTI is on historic Song Wat Road in Bangkok’s old town. Book online, call us or simply walk in."
      heroImage={SAANTI_PHOTOS.contact}
      heroAlt="The entrance to SAANTI Thai Massage on Song Wat Road"
      seoTitle="Contact SAANTI Thai Massage Song Wat | Hours & Directions"
      seoDescription="Find SAANTI Thai Massage at 981 Song Wat Road, Bangkok. See opening hours, telephone, directions and online booking information."
      canonicalPath={`/contact?brand=${brand}`}
      structuredData={activeBranch ? localBusinessSchemas([activeBranch]) : []}
    >
      <section className="spa-page-section spa-contact-page">
        <div className="wrap">
          <div className="spa-contact-intro">
            <div>
              <div className="spa-section-label"><span>01</span> Visit SAANTI</div>
              <h2>Song Wat,<br />Bangkok.</h2>
            </div>
            <p>
              Open every day from 10:00 to 22:00. Online reservations are
              available, and walk-in guests are always welcome.
            </p>
          </div>

          {visibleError && <div className="spa-error">{visibleError}</div>}
          {missingBranch && !error && (
            <div className="spa-error">SAANTI Thai Massage is not accepting online bookings right now.</div>
          )}
          <div className="spa-branch-grid single">
            {activeBranch && (() => {
              const address = activeBranch.address || '981 Song Wat Road, Samphanthawong, Bangkok 10100';
              const phone = formatThaiPhoneDisplay(activeBranch.phone || SAANTI_CONTACT.phoneDisplay);
              return (
                <article key={activeBranch.code}>
                  <div className="eyebrow">SAANTI location</div>
                  <h3>{location.branchName}</h3>
                  <dl>
                    <div><dt>Address</dt><dd>{address}</dd></div>
                    <div><dt>Hours</dt><dd>Daily {activeBranch.hours || '10:00 - 22:00'}</dd></div>
                    <div><dt>Telephone</dt><dd><a href={`tel:${phone.replace(/[^\d+]/g, '')}`}>{phone}</a></dd></div>
                    <div><dt>Line</dt><dd><a href="https://line.me/R/ti/p/@346gsmab" target="_blank" rel="noreferrer">@saantisongwat</a></dd></div>
                  </dl>
                  <div className="spa-contact-actions">
                    <Link className="spa-cta spa-button-link" to={bookingPath}>Book SAANTI</Link>
                    <a
                      className="spa-cta ghost spa-button-link"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${location.branchName} ${address}`)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Directions
                    </a>
                  </div>
                </article>
              );
            })()}
          </div>

          <div className="spa-contact-channels">
            <div>
              <span>Phone</span>
              <a href={SAANTI_CONTACT.phoneHref}>{SAANTI_CONTACT.phoneDisplay}</a>
              <a href="https://wa.me/66955923236" target="_blank" rel="noreferrer">WhatsApp</a>
            </div>
            <div>
              <span>Line</span>
              <a href="https://line.me/R/ti/p/@346gsmab" target="_blank" rel="noreferrer">@saantisongwat</a>
            </div>
            <div>
              <span>Email</span>
              <a href="mailto:sales.saanti@gmail.com">sales.saanti@gmail.com</a>
            </div>
            <div>
              <span>Social</span>
              <a href="https://www.instagram.com/saanti.thaimassage/" target="_blank" rel="noreferrer">@saanti.thaimassage</a>
            </div>
          </div>
        </div>
      </section>
      <BookingBand title="Plan your SAANTI visit." to={bookingPath} />
    </PageShell>
  );
}
